// ----------------------------------------------------------------------------
// Section: Tabbed Cards
// ----------------------------------------------------------------------------
// Tab controls render inside each Tab theme block (where block.settings is
// available). The section outputs an empty tablist shell; JS mounts controls
// into it on init. Panels stay in the panels region — panels are not moved.
// ----------------------------------------------------------------------------

import './_tabbed-cards.scss';
var SECTION_SELECTOR = '[data-section-type="tabbed-cards"]';
var TABLIST_SELECTOR = '[data-tabbed-cards-tablist]';
var TABLIST_WRAP_SELECTOR = '[data-tabbed-cards-tablist-wrap]';
var PANELS_SELECTOR = '[data-tabbed-cards-panels]';
var CONTROL_SELECTOR = '[data-tabbed-cards-control]';
var PANEL_SELECTOR = '[data-tabbed-cards-panel]';
var ACTIVE_CLASS = 'is-active';
var HAS_TABLIST_CLASS = 'tabbed-cards--has-tablist';
function mountControlsIntoTablist(section, tablist) {
  var panelsRegion = section.querySelector(PANELS_SELECTOR);
  if (!panelsRegion) {
    return [];
  }
  var controls = Array.from(panelsRegion.querySelectorAll(CONTROL_SELECTOR));
  for (var control of controls) {
    tablist.appendChild(control);
  }
  return controls;
}
function findPanelForControl(section, control) {
  var blockId = control.dataset.tabBlockId;
  if (blockId) {
    var panelByBlockId = section.querySelector("".concat(PANEL_SELECTOR, "[data-tab-block-id=\"").concat(blockId, "\"]"));
    if (panelByBlockId) {
      return panelByBlockId;
    }
  }
  var panelId = control.getAttribute('aria-controls');
  if (!panelId) {
    return null;
  }
  var panel = document.getElementById(panelId);
  if (!panel || !section.contains(panel)) {
    return null;
  }
  return panel;
}
function pairControlsWithPanels(section, controls) {
  return controls.map(control => {
    var panel = findPanelForControl(section, control);
    if (!panel) {
      control.setAttribute('hidden', '');
      return null;
    }
    return {
      control,
      panel
    };
  }).filter(Boolean);
}
function resolveInitialIndex(controls) {
  var hash = window.location.hash.slice(1);
  if (hash) {
    var hashIndex = controls.findIndex(_ref => {
      var {
        panel
      } = _ref;
      return panel.id === hash;
    });
    if (hashIndex >= 0) {
      return hashIndex;
    }
  }
  var defaultOpenIndex = controls.findIndex(_ref2 => {
    var {
      control
    } = _ref2;
    return control.dataset.defaultOpen === 'true';
  });
  if (defaultOpenIndex >= 0) {
    return defaultOpenIndex;
  }
  return 0;
}
function enforceSingleDefaultOpen(controls, activeIndex) {
  var openByDefault = controls.filter(_ref3 => {
    var {
      control
    } = _ref3;
    return control.dataset.defaultOpen === 'true';
  });
  if (openByDefault.length <= 1) {
    return activeIndex;
  }
  var hash = window.location.hash.slice(1);
  if (hash) {
    var hashIndex = controls.findIndex(_ref4 => {
      var {
        panel
      } = _ref4;
      return panel.id === hash;
    });
    if (hashIndex >= 0) {
      return hashIndex;
    }
  }
  return controls.indexOf(openByDefault[0]);
}
function setActiveTab(controls, activeIndex, preserveScroll) {
  var _controls$;
  var scrollY = preserveScroll ? window.scrollY : null;
  var tablistWrap = (_controls$ = controls[0]) === null || _controls$ === void 0 ? void 0 : _controls$.control.closest(TABLIST_WRAP_SELECTOR);
  var tablistTop = tablistWrap ? tablistWrap.getBoundingClientRect().top : 0;
  controls.forEach((_ref5, index) => {
    var {
      control,
      panel
    } = _ref5;
    var isActive = index === activeIndex;
    control.classList.toggle(ACTIVE_CLASS, isActive);
    control.setAttribute('aria-selected', isActive ? 'true' : 'false');
    control.setAttribute('tabindex', isActive ? '0' : '-1');
    if (isActive) {
      panel.removeAttribute('hidden');
      panel.setAttribute('aria-hidden', 'false');
    } else {
      panel.setAttribute('hidden', '');
      panel.setAttribute('aria-hidden', 'true');
    }
  });
  if (preserveScroll && scrollY !== null && tablistWrap) {
    var delta = tablistWrap.getBoundingClientRect().top - tablistTop;
    window.scrollTo(0, scrollY + delta);
  }
}
function focusTabFromHash(section, controls) {
  var hash = window.location.hash.slice(1);
  if (!hash) {
    return;
  }
  var targetIndex = controls.findIndex(_ref6 => {
    var {
      panel
    } = _ref6;
    return panel.id === hash;
  });
  if (targetIndex < 0) {
    return;
  }
  if (!section.contains(document.getElementById(hash))) {
    return;
  }
  setActiveTab(controls, targetIndex, true);
}
function handleTabKeydown(event, controls, activeIndex, setActive) {
  var {
    key
  } = event;
  var lastIndex = controls.length - 1;
  var nextIndex = activeIndex;
  if (key === 'ArrowRight' || key === 'ArrowDown') {
    nextIndex = activeIndex >= lastIndex ? 0 : activeIndex + 1;
  } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
    nextIndex = activeIndex <= 0 ? lastIndex : activeIndex - 1;
  } else if (key === 'Home') {
    nextIndex = 0;
  } else if (key === 'End') {
    nextIndex = lastIndex;
  } else {
    return;
  }
  event.preventDefault();
  setActive(nextIndex, true);
  controls[nextIndex].control.focus();
}
function initSingleTabSection(section) {
  var panels = Array.from(section.querySelectorAll(PANEL_SELECTOR));
  for (var control of section.querySelectorAll(CONTROL_SELECTOR)) {
    control.setAttribute('hidden', '');
  }
  if (panels.length !== 1) {
    return;
  }
  var [panel] = panels;
  panel.removeAttribute('hidden');
  panel.setAttribute('aria-hidden', 'false');
}
function initSection(section) {
  var tablist = section.querySelector(TABLIST_SELECTOR);
  if (!tablist) {
    initSingleTabSection(section);
    return;
  }
  var mountedControls = mountControlsIntoTablist(section, tablist);
  var controls = pairControlsWithPanels(section, mountedControls);
  section.classList.add(HAS_TABLIST_CLASS);
  if (controls.length === 1) {
    section.classList.add('tabbed-cards--single-tab');
  }
  var activeIndex = resolveInitialIndex(controls);
  activeIndex = enforceSingleDefaultOpen(controls, activeIndex);
  var setActive = function setActive(index) {
    var preserveScroll = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    activeIndex = index;
    setActiveTab(controls, activeIndex, preserveScroll);
  };
  setActive(activeIndex);
  if (controls.length > 1) {
    controls.forEach((_ref7, index) => {
      var {
        control
      } = _ref7;
      control.addEventListener('click', () => {
        setActive(index, true);
      });
      control.addEventListener('keydown', event => {
        handleTabKeydown(event, controls, activeIndex, setActive);
      });
    });
  }
  window.addEventListener('hashchange', () => {
    focusTabFromHash(section, controls);
  });
}
function initTabbedCards() {
  var container = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : document;
  container.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
}
document.addEventListener('DOMContentLoaded', () => {
  initTabbedCards();
});
document.addEventListener('shopify:section:load', _ref8 => {
  var {
    target
  } = _ref8;
  initTabbedCards(target);
});